(ns frontend.ui.component.lp-portal.report.body.real-estate.holding.koke.controller
  (:require
   [frontend.ui.component.chart.utils :as chart-utils]
   [frontend.ui.component.lp-portal.report.controller :as report-ctl]
   [frontend.ui.page.lp-portal.report.util :as util]
   [frontend.ui.style.theme.core :as theme]))

(defn ^{:jsdoc ["@nocollapse"]} point-formatter
  []
  (this-as
   js-data-point
    (let [point (js->clj js-data-point
                         :keywordize-keys
                         true)]
      (str (util/->float-1-decimals (:y point))))))

(defn config->editable-data-investment-at-cost-valuation
  [config]
  {:columns        (some->> config
                            :xAxis
                            (mapcat :categories)
                            (vec))
   :data           (->> config
                        :series
                        (mapv (fn [serie]
                                (vec (concat [(:name serie)]
                                             (:data serie))))))
   :y-axis-config  (chart-utils/config->editable-y-axis-data config)
   :tooltip-config (chart-utils/config->editable-tooltip-data config)})

(defn editable-data->raw-data-investment-at-cost-valuation
  [edited-data]
  (let [data (->> edited-data
                  :data
                  (first)
                  (rest))]
    {:initial-investment         (nth data 0)
     :at-cost                    (nth data 1)
     :valuation-previous-quarter (nth data 2)
     :current-valuation          (nth data 3)}))

(defn- investment-at-cost-valuation
  [{:keys [initial-investment at-cost valuation-previous-quarter current-valuation y-axis-config tooltip-config]}]
  (let [labels ["Initial investment"
                "At cost"
                "Valuation previous quarter"
                "Current valuation"]
        y-axis-1 (chart-utils/resolve-editable-axis-data (first y-axis-config) chart-utils/axis-meur-formatter-no-division "")
        tooltip-1 (chart-utils/resolve-editable-tooltip-data (first tooltip-config) chart-utils/meur-formatter)]
    {:chart       {:type :column}
     :legend      false
     :xAxis       [{:categories    labels
                    :lineColor     (theme/color [:chart :x-axis :line])
                    :gridLineColor (theme/color [:chart :x-axis :grid-line])}]
     :yAxis       [{:title         {:text (:title-text y-axis-1)}
                    :labels        {:formatter (:formatter-fn y-axis-1)}
                    :lineColor     (theme/color [:chart :y-axis :line])
                    :gridLineColor (theme/color [:chart :y-axis :grid-line])}]
     :colors      (or (util/custom-colors)
                      (util/gradient-colors [:lp-report :chart :column :gradient-start]
                                            [:lp-report :chart :column :gradient-end]
                                            4))
     :plotOptions {:series {:dataLabels {:enabled   true
                                         :style     {:color       "contrast"
                                                     :textOutline false}
                                         :formatter point-formatter}}}
     :series      [{:data    [initial-investment at-cost valuation-previous-quarter
                              current-valuation]
                    :tooltip {:outside         true
                              :useHtml         true
                              :backgroundColor (theme/color [:page-bg :bg])
                              :style           {:opacity         1
                                                :backgroundColor (theme/color [:page-bg :bg])}
                              :pointFormatter  (:formatter-fn tooltip-1)}}]}))

(defn config->editable-data-bar-last-n
  [config]
  {:columns        (some->> config
                            :xAxis
                            (mapcat :categories)
                            (vec))
   :data           (->> config
                        :series
                        (mapv (fn [serie]
                                (vec (concat [(:name serie)]
                                             (:data serie))))))
   :y-axis-config  (chart-utils/config->editable-y-axis-data config)
   :tooltip-config (chart-utils/config->editable-tooltip-data config)})

(defn editable-data->raw-data-bar-last-n
  [edited-data]
  {:last-n (count (:columns edited-data))
   :labels (:columns edited-data)
   :values (->> edited-data
                :data
                (keep (fn [row]
                        (vec (rest row))))
                (first))})


(defn bar-last-n
  [{:keys [labels values title y-axis-config tooltip-config last-n]}]
  (let [data (map vector labels values)
        last-n (or last-n 7)                                                                        ;; this was hardcoded in controller
        shown-data (->> data
                        (reverse)
                        (take last-n)
                        (reverse))
        y-axis-1 (chart-utils/resolve-editable-axis-data (first y-axis-config) chart-utils/axis-meur-formatter-no-division title)
        tooltip-1 (chart-utils/resolve-editable-tooltip-data (first tooltip-config) chart-utils/meur-formatter)]
    {:xAxis       [{:categories    (map first shown-data)
                    :lineColor     (theme/color [:chart :x-axis :line])
                    :gridLineColor (theme/color [:chart :x-axis :grid-line])}]
     :yAxis       [{:title         {:text (:title-text y-axis-1)}
                    :lineColor     (theme/color [:chart :y-axis :line])
                    :gridLineColor (theme/color [:chart :y-axis :grid-line])
                    :labels        {:formatter (:formatter-fn y-axis-1)}}]
     :colors      (or (util/custom-colors)
                      (util/gradient-colors [:lp-report :chart :column :gradient-start]
                                            [:lp-report :chart :column :gradient-end]
                                            last-n))
     :plotOptions {:series {:dataLabels {:enabled   true
                                         :style     {:color       "contrast"
                                                     :textOutline false}
                                         :formatter point-formatter}}}
     :legend      false
     :series      [{:type    :column
                    :data    (map second shown-data)
                    :tooltip {:outside         true
                              :useHtml         true
                              :backgroundColor (theme/color [:page-bg :bg])
                              :style           {:opacity         1
                                                :backgroundColor (theme/color [:page-bg :bg])}
                              :pointFormatter  (:formatter-fn tooltip-1)}}]}))

(defn ->ui-data
  [{:keys [report-data] :as props}]
  (let [{:keys [holding-name chart-data]} report-data
        quarterly-figures (:quarterly-figures chart-data)]
    {:holding-name                 holding-name
     :reference-date               (get-in report-data [:reference-date])
     :report-id                    (:report-id props)
     :investment-at-cost-valuation (->> chart-data
                                        (report-ctl/with-footnote props :investment-at-cost-valuation-footnotes)
                                        (report-ctl/with-editable-charts
                                          (assoc props :fixed-columns? true :fixed-labels? true)
                                          :investment-at-cost-valuation
                                          investment-at-cost-valuation
                                          config->editable-data-investment-at-cost-valuation
                                          editable-data->raw-data-investment-at-cost-valuation))
     :noi                          (->>
                                    (assoc quarterly-figures
                                           :values (:noi quarterly-figures)
                                           :last-n 7)
                                    (report-ctl/with-footnote props :noi-footnotes)
                                    (report-ctl/with-editable-charts
                                      props
                                      :noi
                                      bar-last-n
                                      config->editable-data-bar-last-n
                                      editable-data->raw-data-bar-last-n))
     :occupancy                    (->>
                                    (assoc quarterly-figures
                                           :axis-labels {:format "{text} %"}
                                           :values (map #(* % 100) (:occupancy quarterly-figures))
                                           :last-n 7)
                                    (report-ctl/with-footnote props :occupancy-footnotes)
                                    (report-ctl/with-editable-charts
                                      props
                                      :occupancy
                                      bar-last-n
                                      config->editable-data-bar-last-n
                                      editable-data->raw-data-bar-last-n))
     :debt-financing               (->>
                                    (assoc quarterly-figures
                                           :values (:loan-balance quarterly-figures)
                                           :last-n 7)
                                    (report-ctl/with-footnote props :debt-financing-footnotes)
                                    (report-ctl/with-editable-charts
                                      props
                                      :debt-financing
                                      bar-last-n
                                      config->editable-data-bar-last-n
                                      editable-data->raw-data-bar-last-n))}))
